/**
 * EPIC283-D trusted Now presenter.
 * Maps B-ready readiness (+ optional customer-safe K1 focus) onto contentContract.
 * Never invents update_mode, ages, or issue/location labels.
 */

import {
  displayStateContent,
  formatDisplayMessage,
  type DisplayStateKey,
} from './contentContract';
import {
  formatNowReadinessDiagnostics,
  type NowReadinessResult,
  type ReadinessDiagnosticRow,
} from './nowReadiness';

/** Optional customer-safe focus fields — pass through only when K1 supplies them. */
export type NowFocusInput = {
  topIssueLabel?: string | null;
  locationLabel?: string | null;
  /** Supporting bucket activity total — never claim measurementValue / rate. */
  measurementValue?: number | null;
  /** K1 availability is limited even when freshness display_state is current. */
  measurementLimited?: boolean;
};

export type TrustedNowPresentation = {
  stateKey: DisplayStateKey;
  stateLabel: string;
  message: string;
  limitation: string;
  primaryCta: string;
  shiftLabel: string;
  asOfLabel: string;
  topIssueLabel: string | null;
  locationLabel: string | null;
  /** Above-the-fold issue/location line (honest unavailable when missing). */
  issueLocationLine: string;
  /** Declared update-mode summary for methodology disclosure. */
  updateModeSummary: string;
  /** Measurement ≠ condition reminder. */
  methodologySummary: string;
  diagnostics: ReadinessDiagnosticRow[];
};

const KNOWN_STATES: ReadonlySet<string> = new Set([
  'current',
  'current_with_limitations',
  'delayed',
  'stale',
  'never_measured',
  'scan_only_collecting',
  'unavailable',
  'readiness_unavailable',
  'empty',
  'blocked',
  'unauthorized',
  'failure',
]);

function mapDisplayStateKey(raw: string | null): DisplayStateKey {
  if (!raw) return 'unavailable';
  const normalized = raw.trim().toLowerCase().replace(/-/g, '_');
  if (normalized === 'current_with_limitation') return 'current_with_limitations';
  if (KNOWN_STATES.has(normalized)) return normalized as DisplayStateKey;
  return 'unavailable';
}

function resolveIssueLocation(focus?: NowFocusInput | null): {
  topIssueLabel: string | null;
  locationLabel: string | null;
  issueLocationLine: string;
} {
  const measured =
    focus?.measurementValue != null && Number.isFinite(focus.measurementValue)
      ? `Qualifying activity this shift: ${focus.measurementValue}. `
      : '';
  const top =
    typeof focus?.topIssueLabel === 'string' && focus.topIssueLabel.trim()
      ? focus.topIssueLabel.trim()
      : null;
  const loc =
    typeof focus?.locationLabel === 'string' && focus.locationLabel.trim()
      ? focus.locationLabel.trim()
      : null;
  if (top && loc) {
    return {
      topIssueLabel: top,
      locationLabel: loc,
      issueLocationLine: `${measured}${top} · ${loc}`,
    };
  }
  if (top) {
    return {
      topIssueLabel: top,
      locationLabel: null,
      issueLocationLine: `${measured}${top} · Location not available from site-state`,
    };
  }
  return {
    topIssueLabel: null,
    locationLabel: null,
    issueLocationLine:
      `${measured}Top issue and location are not available from the site-state contract yet.`,
  };
}

function modeLimitation(readiness: NowReadinessResult, baseLimitation: string): string {
  if (readiness.updateMode === 'scan_only') {
    const next = readiness.nextTriggerDisplay;
    const nextLine =
      next === 'Not scheduled'
        ? 'Next update is not scheduled.'
        : next && next !== 'Unavailable' && next !== 'N/A'
          ? `Next update: ${next}.`
          : 'Next update trigger is unavailable.';
    return [
      'Scan-completion producer; not an incremental current view.',
      nextLine,
      baseLimitation,
    ]
      .filter(Boolean)
      .join(' ');
  }
  if (readiness.updateMode === 'incremental') {
    return ['Incremental update mode is declared for this Now window.', baseLimitation]
      .filter(Boolean)
      .join(' ');
  }
  return baseLimitation;
}

function updateModeSummary(readiness: NowReadinessResult): string {
  if (readiness.updateMode === 'scan_only') {
    return `Declared update mode: scan-only. Next trigger: ${readiness.nextTriggerDisplay}.`;
  }
  if (readiness.updateMode === 'incremental') {
    return 'Declared update mode: incremental.';
  }
  return 'Declared update mode unavailable.';
}

/**
 * Present Trusted Now for a B-ready readiness result.
 * Callers must gate on mayClaimTrustedNow — blocked Now stays on readiness_unavailable.
 */
export function presentTrustedNow(params: {
  readiness: NowReadinessResult;
  focus?: NowFocusInput | null;
  dataNote?: string | null;
}): TrustedNowPresentation {
  if (!params.readiness.mayClaimTrustedNow) {
    throw new Error('presentTrustedNow requires mayClaimTrustedNow');
  }

  const asOf = params.readiness.asOf || '—';
  const stateKey = mapDisplayStateKey(params.readiness.displayState);
  const block = displayStateContent(stateKey);
  const message = formatDisplayMessage(stateKey, asOf);
  const governedLimitation =
    params.focus?.measurementLimited && stateKey === 'current'
      ? 'Site-state marks this measurement window as limited.'
      : block.limitation;
  let limitation = modeLimitation(params.readiness, governedLimitation);
  if (params.dataNote) {
    limitation = `${limitation} ${params.dataNote}`.trim();
  }

  const focus = resolveIssueLocation(params.focus);

  return {
    stateKey,
    stateLabel: block.label,
    message,
    limitation,
    primaryCta: block.primaryCta,
    shiftLabel: 'Current shift',
    asOfLabel: asOf,
    topIssueLabel: focus.topIssueLabel,
    locationLabel: focus.locationLabel,
    issueLocationLine: focus.issueLocationLine,
    updateModeSummary: updateModeSummary(params.readiness),
    methodologySummary:
      'Measurement currentness describes evidence freshness — not whether site conditions are good or bad.',
    diagnostics: formatNowReadinessDiagnostics(params.readiness),
  };
}
